import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - Burger menu disclosure
 * @property {HTMLElement} summary - Burger toggle
 */

/**
 * Absolut Detail header.
 * Enhances the native `<details>` burger menu (Escape closes and returns focus, closes on
 * link click or when the desktop layout applies, locks page scroll while open) and flags
 * the bar once the page is scrolled so it can show a light shadow.
 *
 * @extends {Component<Refs>}
 */
class AbsolutHeader extends Component {
  requiredRefs = ['details', 'summary'];

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { details } = this.refs;

    details.addEventListener('toggle', this.#handleToggle, { signal });
    details.addEventListener('click', this.#handleDrawerClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window
      .matchMedia('(min-width: 1200px)')
      .addEventListener('change', (event) => event.matches && this.close(), { signal });

    this.#handleScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    document.documentElement.classList.remove('ab-menu-open');
  }

  /**
   * Closes the burger menu.
   * @param {{ focus?: boolean }} [options] - `focus: true` moves focus back to the toggle
   */
  close({ focus = false } = {}) {
    const { details, summary } = this.refs;
    if (!details.open) return;

    details.open = false;
    if (focus) summary.focus();
  }

  #handleToggle = () => {
    document.documentElement.classList.toggle('ab-menu-open', this.refs.details.open);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.refs.details.open) return;

    event.preventDefault();
    this.close({ focus: true });
  };

  /** @param {MouseEvent} event */
  #handleDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.ab-header__drawer a')) this.close();
  };

  #handleScroll = () => {
    this.toggleAttribute('data-scrolled', window.scrollY > 8);
  };
}

if (!customElements.get('absolut-header')) {
  customElements.define('absolut-header', AbsolutHeader);
}
